import type { ServiceRequest } from "fhir/r5";
import { VALUE_LABELS, ValueCode } from "../models/OverviewDashboard";
import { getUpCareTheme, UpCareThemeCode } from "../utils/UpCareThemes";

const VALUE_SYSTEM = "https://chunancy.fr/fhir/CodeSystem/upcare-values";
const REQUEST_TYPE_SYSTEM = "https://chunancy.fr/fhir/CodeSystem/upcare-request-type";
const ACTION_TYPE_SYSTEM = "https://chunancy.fr/fhir/CodeSystem/upcare-action-type";
export const CONCERNED_ORGANIZATION_EXTENSION_URL =
    "https://chunancy.fr/fhir/StructureDefinition/upcare-concerned-organization";

const CONCERNED_TEAM_ID = "concerned-team";

export type NeedPriority = "high" | "medium" | "low";
export type ProposalActionType = "atelier" | "formation" | "bonne-pratique";

export interface CreateNeedInput {
    organizationReference: string;
    organizationDisplay?: string;
    value: ValueCode;
    theme: UpCareThemeCode;
    priority: NeedPriority;
    description: string;
}

export interface CreateProposalInput {
    organizationReference: string;
    organizationDisplay?: string;
    value: ValueCode;
    theme: UpCareThemeCode;
    actionType: ProposalActionType;
    title: string;
    description: string;
}

class ActionPlanCreationService {
    private fhirBaseUrl: string;

    constructor(fhirBaseUrl: string) {
        this.fhirBaseUrl = fhirBaseUrl.replace(/\/$/, "");
    }

    createNeed(input: CreateNeedInput): Promise<ServiceRequest> {
        const description = input.description.trim();
        const resource = {
            resourceType: "ServiceRequest",
            contained: [this.createConcernedTeam(input)],
            extension: [this.createConcernedOrganizationExtension(input)],
            status: "active",
            intent: "order",
            priority: mapPriority(input.priority),
            code: {
                coding: [{
                    system: REQUEST_TYPE_SYSTEM,
                    code: "besoin",
                    display: "Besoin"
                }],
                text: description
            },
            category: [this.createThemeCodeableConcept(input.theme)],
            subject: {
                reference: `#${CONCERNED_TEAM_ID}`,
                display: this.getConcernedTeamName(input)
            },
            requester: {
                reference: input.organizationReference,
                display: input.organizationDisplay
            },
            reasonCode: [{
                coding: [{
                    system: VALUE_SYSTEM,
                    code: input.value,
                    display: VALUE_LABELS[input.value]
                }],
                text: VALUE_LABELS[input.value]
            }],
            authoredOn: new Date().toISOString(),
            note: [{ text: description }]
        } as unknown as ServiceRequest;

        return this.create(resource);
    }

    createProposal(input: CreateProposalInput): Promise<ServiceRequest> {
        const title = input.title.trim();
        const description = input.description.trim();
        const actionTypeLabel = ACTION_TYPE_LABELS[input.actionType];
        const resource = {
            resourceType: "ServiceRequest",
            contained: [this.createConcernedTeam(input)],
            extension: [this.createConcernedOrganizationExtension(input)],
            status: "active",
            intent: "proposal",
            code: {
                coding: [
                    {
                        system: REQUEST_TYPE_SYSTEM,
                        code: "proposition",
                        display: "Proposition"
                    },
                    {
                        system: ACTION_TYPE_SYSTEM,
                        code: input.actionType,
                        display: actionTypeLabel
                    }
                ],
                text: title
            },
            category: [this.createThemeCodeableConcept(input.theme)],
            subject: {
                reference: `#${CONCERNED_TEAM_ID}`,
                display: this.getConcernedTeamName(input)
            },
            requester: {
                reference: input.organizationReference,
                display: input.organizationDisplay
            },
            reasonCode: [{
                coding: [{
                    system: VALUE_SYSTEM,
                    code: input.value,
                    display: VALUE_LABELS[input.value]
                }],
                text: VALUE_LABELS[input.value]
            }],
            authoredOn: new Date().toISOString(),
            note: [{ text: description }]
        } as unknown as ServiceRequest;

        return this.create(resource);
    }

    private createThemeCodeableConcept(themeCode: UpCareThemeCode) {
        const theme = getUpCareTheme(themeCode);
        if (!theme) {
            throw new Error(`Thématique UP CARE inconnue : ${themeCode}`);
        }

        return {
            coding: [{
                system: theme.system,
                code: theme.code,
                display: theme.display
            }],
            text: theme.display
        };
    }

    private createConcernedOrganizationExtension(input: {
        organizationReference: string;
        organizationDisplay?: string;
    }) {
        return {
            url: CONCERNED_ORGANIZATION_EXTENSION_URL,
            valueReference: {
                reference: input.organizationReference,
                display: input.organizationDisplay
            }
        };
    }

    private createConcernedTeam(input: { organizationDisplay?: string }) {
        return {
            resourceType: "Group",
            id: CONCERNED_TEAM_ID,
            type: "practitioner",
            actual: true,
            name: this.getConcernedTeamName(input)
        };
    }

    private getConcernedTeamName(input: { organizationDisplay?: string }): string {
        return input.organizationDisplay
            ? `Équipe - ${input.organizationDisplay}`
            : "Équipe concernée";
    }

    private async create(resource: ServiceRequest): Promise<ServiceRequest> {
        const response = await fetch(`${this.fhirBaseUrl}/ServiceRequest`, {
            method: "POST",
            headers: {
                Accept: "application/fhir+json",
                "Content-Type": "application/fhir+json"
            },
            body: JSON.stringify(resource)
        });

        if (!response.ok) {
            const body = await response.text();
            throw new Error(body || `Erreur FHIR ${response.status}`);
        }

        const persisted = await response.json() as ServiceRequest;
        if (persisted.resourceType !== "ServiceRequest") {
            throw new Error("Le serveur n'a pas retourné le ServiceRequest créé.");
        }

        return persisted;
    }
}

const ACTION_TYPE_LABELS: Record<ProposalActionType, string> = {
    atelier: "Atelier",
    formation: "Formation",
    "bonne-pratique": "Bonne pratique"
};

const mapPriority = (priority: NeedPriority): ServiceRequest["priority"] => {
    if (priority === "high") return "asap";
    if (priority === "medium") return "urgent";
    return "routine";
};

export default ActionPlanCreationService;
